import { useState } from "react";
import { Button } from "reactstrap";

import { apiSlice } from "app/apiSlice";
import { showToast } from "components/Toasts/helpers/showToast";
import { type MilestoneApproval } from "../types";

interface ApproveMilestoneCellRendererProps {
  data: MilestoneApproval;
  canApprovePayment: boolean;
}

/**
 * Cell renderer for individual milestone approval actions
 * Handles approve/unapprove functionality for single milestones
 * Column is tied to isPaymentApproved field for automatic refresh
 */
export const ApproveMilestoneCellRenderer = ({
  data,
  canApprovePayment,
}: ApproveMilestoneCellRendererProps) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMilestoneApproval] =
    apiSlice.useUpdateMilestoneApprovalMutation();

  const isApproved = data.isPaymentApproved;

  const handleApprovalToggle = async () => {
    if (!data._id || !canApprovePayment) return;

    setIsUpdating(true);

    try {
      const newApprovalStatus = !isApproved;
      await updateMilestoneApproval({
        milestoneIds: [data._id],
        isPaymentApproved: newApprovalStatus,
      }).unwrap();

      showToast({
        type: "success",
        message: `Milestone ${newApprovalStatus ? "approved" : "unapproved"} successfully.`,
      });
    } catch (error: any) {
      showToast({
        type: "error",
        message:
          error?.data?.message ||
          `Failed to ${isApproved ? "unapprove" : "approve"} milestone. Please try again.`,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  if (!canApprovePayment) {
    return (
      <span className="text-muted small">
        {isApproved ? "Approved" : "Not Approved"}
      </span>
    );
  }

  return (
    <Button
      color={isApproved ? "danger" : "success"}
      size="sm"
      outline
      onClick={handleApprovalToggle}
      disabled={isUpdating}
    >
      {isUpdating ? "Updating..." : isApproved ? "Unapprove" : "Approve"}
    </Button>
  );
};

ApproveMilestoneCellRenderer.displayName = "ApproveMilestoneCellRenderer";
